import { useMemo, useRef } from 'react';

import { useGetLatestAccuracyJobsQuery } from '../../generate-accuracy/api/accuracyApiSlice';
import {
  ACCURACY_JOB_TERMINAL_STATUSES,
  ACCURACY_POLL_INTERVAL_MS,
} from '../../generate-accuracy/constants';
import type { AccuracyJobSummaryRow } from '../../generate-accuracy/types/response';

type LatestAccuracyJobsResult = ReturnType<typeof useGetLatestAccuracyJobsQuery>;

export interface UseAccuracyJobColumnResult {
  jobsByProjectId: Map<string, AccuracyJobSummaryRow>;
  isLoading: boolean;
  error: LatestAccuracyJobsResult['error'];
}

/**
 * One polling subscription for the whole Projects grid — see the design doc's
 * Polling section. A per-row query hook would be one cache key and one timer per
 * row, and RTK Query does not coalesce across cache keys, so a 25-row page would
 * issue 25 requests per tick.
 *
 * `pollingInterval` is read from a ref rather than computed inline from `data`,
 * since the value returned by this same call isn't available until after the
 * call returns — the ref carries the previous render's `anyActive` verdict
 * forward, and RTK Query re-reads it on every render to schedule the next tick.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const useAccuracyJobColumn = (canGenerateAccuracy: boolean): UseAccuracyJobColumnResult => {
  const pollingIntervalRef = useRef(ACCURACY_POLL_INTERVAL_MS);
  const maxPercentByJobId = useRef(new Map<string, number>());

  const { data, isLoading, error } = useGetLatestAccuracyJobsQuery(undefined, {
    skip: !canGenerateAccuracy,
    pollingInterval: pollingIntervalRef.current,
    skipPollingIfUnfocused: true,
  });

  const rows = data?.data;
  const anyActive = rows?.some((row) => !ACCURACY_JOB_TERMINAL_STATUSES.has(row.status)) ?? false;
  pollingIntervalRef.current = anyActive ? ACCURACY_POLL_INTERVAL_MS : 0;

  const jobsByProjectId = useMemo(() => {
    const map = new Map<string, AccuracyJobSummaryRow>();
    (rows ?? []).forEach((job) => {
      if (!job.progress) {
        map.set(job.projectId, job);
        return;
      }
      // Backend percent isn't guaranteed monotonic — clamp per job so the bar never
      // appears to move backwards on a later poll.
      const previousMax = maxPercentByJobId.current.get(job.accuracyJobId) ?? 0;
      const percent = Math.max(previousMax, job.progress.percent);
      maxPercentByJobId.current.set(job.accuracyJobId, percent);
      map.set(job.projectId, { ...job, progress: { ...job.progress, percent } });
    });
    return map;
  }, [rows]);

  return { jobsByProjectId, isLoading, error };
};
